import React from "react";
import PropTypes from "prop-types";
import getUnixTime from "date-fns/getUnixTime";

import { SanitizeHTML } from "@components/sanitizeHtml";
import { SectionHeading } from "@components/headings/section";
import { ImgixNextImage } from "@components/imgixNextImage";
import { SocialRow } from "@components/socialRow";
import { MapPin } from "@icons";

import { formatArticleDate } from "@utils/formatArticleDate";
import { makeLpTitle } from "@utils/makeLpTitle";
import { dangerHTML } from "@utils/danger";

import { authorAPI, validateAuthor } from "@queries/authors/api";
import { LP_URL } from "@constants/lpUrl";
import { NextHead } from "@components/nextHead";

export default function AuthorDetailPage({ author, articles, canonicalUrl }) {
  const {
    bio,
    city,
    country,
    externalLink,
    firstName,
    image,
    lastName,
    socialMediaLinks,
  } = author;

  return (
    <>
      <NextHead>
        <link key="canonical" rel="canonical" href={canonicalUrl} />
        <title>{makeLpTitle(`${firstName} ${lastName}`)}</title>
        <script
          type="application/ld+json"
          {...dangerHTML({
            __html: JSON.stringify({
              "@context": "http://www.schema.org",
              "@type": "Person",
              name: `${firstName} ${lastName}`,
              image: `${image ? image.url : ""}`,
              address: {
                "@type": "PostalAddress",
                addressLocality: `${city}`,
                addressCountry: `${country}`,
              },
              affiliation: [
                {
                  "@type": "Organization",
                  name: "Lonely Planet",
                  sameAs: [LP_URL],
                },
              ],
              jobTitle: "Editor",
              url: `${LP_URL}/authors/${firstName?.toLowerCase()}-${lastName?.toLowerCase()}`,
              ...(socialMediaLinks.length
                ? { sameAs: socialMediaLinks.map(social => social.href) }
                : null),
            }),
          })}
        />
      </NextHead>
      <div className="container flex items-center my-12 lg:my-16">
        <ImgixNextImage
          className="mr-4 rounded-full w-24 lg:w-40 aspect-square"
          alt={`${firstName} ${lastName}`}
          {...(image?.url ? { src: image.url } : {})}
          width={300}
          height={300}
          imgixParams={{
            crop: "faces",
            w: 300,
            h: 300,
          }}
        />
        <div className="lg:flex flex-col">
          <SectionHeading elem="h1" className="lg:text-2xl">
            {firstName} {lastName}
          </SectionHeading>
          <div className="md:flex items-center text-sm font-semibold">
            {(city || country) && (
              <p className="text-black-400 my-2 lg:my-0 lg:mr-2">
                <MapPin
                  className="text-blue inline-block mr-2"
                  height="20"
                  width="20"
                />
                {city}
                {city && country && ", "}
                {country}
              </p>
            )}
            {externalLink?.title && (
              <a rel="noopener noreferrer" href={externalLink?.url}>
                {externalLink.title}
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="container lg:flex items-start my-12 lg:my-16">
        {(bio || socialMediaLinks?.[0]) && (
          <div className="lg:w-2/4 mb-4 pr-4 flex-initial">
            {bio && (
              <article>
                <SectionHeading elem="h2" minor>
                  About {firstName}
                </SectionHeading>
                <SanitizeHTML
                  className="biography mt-5"
                  html={bio}
                  elem="div"
                />
                <style jsx>{`
                  :global(.biography p + p) {
                    margin-top: 12px;
                  }
                `}</style>
              </article>
            )}
            {socialMediaLinks?.[0] && (
              <article className="mt-10">
                <SectionHeading elem="h3" className="pb-4" minor>
                  Connect with {firstName}
                </SectionHeading>
                <SocialRow socials={socialMediaLinks} />
              </article>
            )}
          </div>
        )}
        <div className="lg:w-1/2 lg:pl-4 flex-initial">
          <SectionHeading elem="h3" minor>
            Recent stories from {firstName}
          </SectionHeading>
          {!articles?.length ? (
            <p>No stories...</p>
          ) : (
            <ul>
              {articles.map(
                ({
                  date,
                  featuredImage,
                  readTime,
                  slug: articleSlug,
                  title,
                }) => (
                  <li key={articleSlug} className="my-5">
                    <div className="flex">
                      <div className="flex-none mr-5">
                        {featuredImage?.url && (
                          <ImgixNextImage
                            alt={featuredImage?.alt}
                            className="rounded"
                            src={featuredImage?.url}
                            width={100}
                            height={100}
                            imgixParams={{
                              w: 140,
                              h: 140,
                              fit: "crop",
                              ar: "1:1",
                            }}
                          />
                        )}
                      </div>
                      <div>
                        <h3>
                          <a
                            href={`/articles/${articleSlug}`}
                            className="block lg:text-lg line-clamp-2 font-semibold text-blue hover:text-[#267DFF]"
                          >
                            {title}
                          </a>
                        </h3>
                        <p className="text-black-400 text-xs font-bold">
                          {readTime} min read &bull;{" "}
                          {formatArticleDate(getUnixTime(new Date(date)))}
                        </p>
                      </div>
                    </div>
                  </li>
                )
              )}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
export async function getServerSideProps({ query, res }) {
  res.setHeader("Cache-Control", "no-cache, max-age=3600, must-revalidate");
  const { slug } = query;

  if (!slug) {
    return {
      notFound: true,
    };
  }

  const { isValid, redirect } = await validateAuthor({ slug });

  if (!isValid) {
    return {
      notFound: true,
    };
  }

  if (redirect) {
    return {
      redirect: {
        destination: redirect,
        permanent: true,
      },
    };
  }

  const data = await authorAPI({ slug });

  const { author, articles } = data;
  const canonicalUrl = `${LP_URL}/authors/${slug}`;

  return {
    props: {
      author,
      articles: articles?.items,
      canonicalUrl,
    },
  };
}

AuthorDetailPage.propTypes = {
  author: PropTypes.shape({
    bio: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    email: PropTypes.string,
    externalLink: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    image: PropTypes.shape({
      alt: PropTypes.string,
      caption: PropTypes.string,
      credit: PropTypes.string,
      height: PropTypes.number,
      name: PropTypes.string,
      title: PropTypes.string,
      url: PropTypes.string,
      width: PropTypes.number,
    }),
    socialMediaLinks: PropTypes.arrayOf(
      PropTypes.shape({
        href: PropTypes.string,
        label: PropTypes.string,
      })
    ),
  }).isRequired,

  articles: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      featuredImage: PropTypes.shape({
        alt: PropTypes.string,
        url: PropTypes.string,
      }),
      readTime: PropTypes.number,
      slug: PropTypes.string,
      title: PropTypes.string,
    })
  ).isRequired,
  canonicalUrl: PropTypes.string.isRequired,
};
